import { DBFilterValues } from 'apolloClient/types/Searches';
import { priceFormatter } from 'src/utils/utils';

type LocationData = {
  data: Array<{
    attributes?: {
      name?: string;
      primaryAddress?: string;
    };
  }>;
};

type ValueType = string | number | undefined;

const toNumber = (value: ValueType): number | undefined => {
  return typeof value === 'string' ? Number(value) : value;
};

const formatRange = (min: ValueType, max: ValueType, unit: string): string | null => {
  const minNum = toNumber(min);
  const maxNum = toNumber(max);

  if (!minNum && !maxNum) return null;
  if (minNum && !maxNum) return `${minNum}+ ${unit}`;
  if (!minNum && maxNum) return `< ${maxNum} ${unit}`;
  if (minNum === maxNum) return `${minNum} ${unit}`;
  return `${minNum}-${maxNum} ${unit}`;
};

const formatPriceRange = (min: ValueType, max: ValueType, suffix = ''): string | null => {
  const minNum = toNumber(min);
  const maxNum = toNumber(max);

  if (!minNum && !maxNum) return null;
  if (minNum && !maxNum) return `${priceFormatter(minNum)}+${suffix}`;
  if (!minNum && maxNum) return `<${priceFormatter(maxNum)}${suffix}`;
  if (minNum === maxNum) return `${priceFormatter(minNum!)}${suffix}`;
  return `${priceFormatter(minNum!)}-${priceFormatter(maxNum!)}${suffix}`;
};

const getLocationNames = (data: LocationData | undefined, type: string): string[] => {
  if (!data?.data?.length) return [];
  return data.data
    .map(item => item?.attributes?.name)
    .filter((name): name is string => !!name);
};

function millisecondsToMonths(ms: string): number {
  const msInMonth = 1000 * 60 * 60 * 24 * 30.44;
  return Math.floor(Number(ms) / msInMonth);
}

const DAYS_ON_SITE_MAP: Record<number, string> = {
  1: '1 day',
  7: '7 days',
  14: '14 days',
  30: '30 days',
  90: '90 days',
  182: '6 months',
  365: '12 months',
  730: '24 months',
  1095: '36 months'
};

const getViewCriteria = (param: Partial<DBFilterValues>): string[] => {
  const { skylineView, oceanView, intracoastalView } = param;
  const views = [];

  oceanView && views.push('Ocean View');
  skylineView && views.push('Skyline View');
  intracoastalView && views.push('Bay/Intracoastal View');

  return views.length ? [views.join(' OR ')] : [];
};

const getWaterfrontCriteria = (param: Partial<DBFilterValues>): string[] => {
  const { wfAny, beachfront, oceanfront } = param;
  const waterfront = [];

  wfAny && waterfront.push('All waterfront');
  beachfront && waterfront.push('Beachfront');
  oceanfront && waterfront.push('Oceanfront');

  return waterfront.length ? [waterfront.join(' OR ')] : [];
};

const getSecretSearch = (searchText: string): string | null => {
  const search = searchText.replace(/^"|"$/g, '');
  const terms = search.split(',').map(term => term.trim());

  const includes: string[] = [];
  const excludes: string[] = [];

  terms.forEach(term => {
    if (term.startsWith('!')) {
      const cleanTerm = term.slice(1).replace(/^"|"$/g, '');
      excludes.push(cleanTerm);
    } else {
      const cleanTerm = term.replace(/^"|"$/g, '');
      includes.push(cleanTerm);
    }
  });

  const searchParts = [
    ...includes.length ? [`Contains: ${includes.join(' AND ')}`] : [],
    ...excludes.length ? [`Excludes: ${excludes.join(' AND ')}`] : []
  ];

  return searchParts.length ? `MLS Remarks [${searchParts.join(', ')}]` : null;
};

const formatShortTermRentals = (param: Partial<DBFilterValues>): string | null => {
  if (!param.shortTermRentTerms?.length) return null;

  const terms = param.shortTermRentTerms
    .map(term => `${term.term} times/year`)
    .join(', ');

  return `Allows Short Term Rentals At Least: ${terms}`;
};

const subSaleCriteria = [
  { key: 'preConstruction', label: 'Pre-construction' },
  { key: 'newConstruction', label: 'New construction' },
  { key: 'foreclosures', label: 'Bank-owned' },
  { key: 'shortSales', label: 'Short Sales' },
  { key: 'existingCondos', label: 'Existing condos' }
] as const;

export function getSearchesCriteria(param: Partial<DBFilterValues> = {}) {
  const criteria: string[] = [];

  param?.addresses?.data[0]?.attributes?.primaryAddress &&
    criteria.push(param.addresses.data[0].attributes.name);

  criteria.push(
    ...getLocationNames(param.neighborhoods, 'neighborhoods'),
    ...getLocationNames(param.buildings, 'buildings'),
    ...getLocationNames(param.cities, 'cities'),
    ...getLocationNames(param.counties, 'counties'),
    ...(param.zipCodes?.filter(zip => zip?.code).map(zip => zip.code) || [])
  );

  param.sold && criteria.push(`Sold`);

  const enabledSubCriteria = subSaleCriteria
    .filter(item => param[item.key])
    .map(item => item.label);

  if (param.forSale && enabledSubCriteria.length === subSaleCriteria.length) {
    criteria.push('For sale');
  } else {
    criteria.push(...enabledSubCriteria);
  }

  param.forRent && criteria.push(`For rent`);
  param?.furnished && criteria.push(`Furnished`);
  param?.unfurnished && criteria.push(`Unfurnished`);

  const priceRange = formatPriceRange(param.minPrice, param.maxPrice);
  priceRange && criteria.push(priceRange);

  const bedsRange = formatRange(param.minBeds, param.maxBeds, 'beds');
  bedsRange && criteria.push(bedsRange);

  param.maxHOA && criteria.push(`Max HOA Fee ${param.maxHOA}`);

  const floorRange = formatRange(param.minFloor, param.maxFloor, 'floors');
  floorRange && criteria.push(floorRange);

  param.baths && criteria.push(`${param.baths}+ baths`);

  const sqftRange = formatRange(param.minSqft, param.maxSqft, 'sq ft');
  sqftRange && criteria.push(sqftRange);

  const yearRange = formatRange(param.minYearBuild, param.maxYearBuild, 'Year Built');
  yearRange && criteria.push(yearRange);

  const priceSqFtRange = formatPriceRange(param.minPriceSqFt, param.maxPriceSqFt, '/sq ft');
  priceSqFtRange && criteria.push(priceSqFtRange);

  criteria.push(...getViewCriteria(param));
  criteria.push(...getWaterfrontCriteria(param));

  const unitsRange = formatRange(param.minUnitsInBuilding, param.maxUnitsInBuilding, 'Units in building');
  unitsRange && criteria.push(unitsRange);

  param.parkingPlaces && criteria.push(`Parking space ${param.parkingPlaces}+`);

  if (param.daysOnCbb && DAYS_ON_SITE_MAP[param.daysOnCbb]) {
    criteria.push(`Days On site: ${DAYS_ON_SITE_MAP[param.daysOnCbb]}`);
  }

  param?.closedAt && criteria.push(`Sold in last: ${millisecondsToMonths(param.closedAt)} months`);

  param?.keyWords && criteria.push(param?.keyWords);
  param?.petFriendly && criteria.push(`Pet-Friendly`);
  param?.penthouse && criteria.push(`Penthouse`);
  param?.bitcoin && criteria.push(`Accepts Bitcoin`);
  param?.rentalInvestments && criteria.push(`Rental Investments`);
  param?.sellerFinancing && criteria.push(`Seller Financing`);
  param?.showPendingListings && criteria.push(`Show Pending Listings`);
  param?.corner && criteria.push(`Corner Unit`);
  param?.unitNumberRegex && criteria.push(`Lines ${param?.unitNumberRegex}`);

  const secretSearch = param.secretSearch && getSecretSearch(param.secretSearch);
  secretSearch && criteria.push(secretSearch);

  param?.shortTermRent && criteria.push(`Allows Short Term Rentals`);

  const shortTermRentalTerms = formatShortTermRentals(param);
  shortTermRentalTerms && criteria.push(shortTermRentalTerms);

  return criteria.join(', ');
}
